/*!

=========================================================
* Argon Design System React - v1.1.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-design-system-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-design-system-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useEffect, useRef, useState } from "react";

// reactstrap components
// The card and buttons are brand-auth primitives now; only the layout grid
// and the modal are still Argon's.
import { Container, Row, Col, Modal, ModalHeader, ModalBody } from "reactstrap";
import "../../styles/brand-auth.css";

// core components
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";
import { useUserAuth } from "../../context/UserContext";
import { useNavigate } from "react-router-dom";
import { getAvatars, resolveAvatar, DEFAULT_AVATAR_ID } from "../../constants/avatars";
import { updateProfileAvatar } from "../../services/userService";
import { auth } from "../../config/firebase-config";
import useReveal from "hooks/useReveal";
import { useTranslation } from "react-i18next";
import "./Profile.css";

const Profile = () => {

  // The panel's entrance — see styles/premium.css.
  useReveal();

  const { logOut, user, setUser } = useUserAuth();
  const { t } = useTranslation();
  const test = "test";
  const mainRef = useRef(null);
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);

  const currentAvatarId = user?.avatarId || DEFAULT_AVATAR_ID;

  const handleSelectAvatar = async (avatarId) => {
    setPickerOpen(false);
    if (!user || avatarId === currentAvatarId) return;
    try {
      // Get a fresh ID token (survives page reloads and works for Google sign-in).
      const token = auth.currentUser
        ? await auth.currentUser.getIdToken()
        : user.accessToken;
      await updateProfileAvatar(avatarId, token);
      setUser({ ...user, avatarId });
    } catch (error) {
      console.error("Failed to update profile image:", error);
    }
  };




  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    mainRef.current.scrollTop = 0;
  }, []);



  return (
    <>
      <DemoNavbar />
      <main className="cn-cover-page profile-page" ref={mainRef}>

        {/* The navy cover replaces Argon's bright purple one. The skewed white
            SVG separator went with it — the panel's own rounded top edge is
            what breaks the band now. */}
        <section className="cn-cover cn-grid">
          <span className="cn-grain" aria-hidden="true" />
          <span className="cn-orb cn-orb-1" />
          <span className="cn-orb cn-orb-2" />
          <span className="cn-orb cn-orb-3" />
        </section>

        <Container className="cn-profile-shell">
          <div className="cn-panel cn-profile-panel" data-reveal>
            <a
              href="#change-avatar"
              className="profile-avatar"
              onClick={(e) => {
                e.preventDefault();
                setPickerOpen(true);
              }}
              title={t("profile.changeImage")}
              aria-label={t("profile.changeImage")}
            >
              <img alt="" src={resolveAvatar(currentAvatarId)} />
              <span className="profile-avatar__overlay" aria-hidden="true">
                <i className="fa fa-camera" />
                <span>{t("profile.change")}</span>
              </span>
            </a>

            <div className="cn-profile-identity">
              <h1 className="cn-panel-title">
                {user.name} {user.last}
              </h1>
              <p className="cn-profile-email">{t("login.email")}: {user.email}</p>
            </div>

            <hr className="cn-panel-rule" />

            <div className="cn-profile-actions">
              <button
                className="cn-btn-primary"
                type="button"
                onClick={() => navigate("/accountsettings")}
              >
                {t("pageTitles.accountSettings")}
              </button>
            </div>

            {/* CODE BELLOW IS A GOOD IDEA FOR ADDING AWARDS AND CERTIFICATES */}
          </div>
        </Container>
      </main>

      <Modal
        isOpen={pickerOpen}
        toggle={() => setPickerOpen(false)}
        centered
        contentClassName="cn-avatar-modal"
        labelledBy="avatar-picker-title"
      >
        <ModalHeader toggle={() => setPickerOpen(false)} closeAriaLabel={t("deleteAccount.close")}>
          <span id="avatar-picker-title">{t("profile.chooseImage")}</span>
        </ModalHeader>
        <ModalBody>
          <Row>
            {getAvatars().map((avatar) => {
              const isSelected = avatar.id === currentAvatarId;
              return (
                <Col xs="4" className="text-center mb-4" key={avatar.id}>
                  {/* A real button, named by its visible label; aria-pressed
                      marks the current avatar. Reset so it looks as before. */}
                  <button
                    type="button"
                    className="d-inline-block p-0 border-0 bg-transparent"
                    style={{ font: "inherit", color: "inherit", textAlign: "center" }}
                    aria-pressed={isSelected}
                    onClick={() => handleSelectAvatar(avatar.id)}
                  >
                    <img
                      alt=""
                      src={avatar.src}
                      className={
                        "cn-avatar-option" + (isSelected ? " is-selected" : "")
                      }
                    />
                    <span className="d-block mt-2 small text-muted">{avatar.label}</span>
                  </button>
                </Col>
              );
            })}
          </Row>
        </ModalBody>
      </Modal>

      <MainFooter />
    </>
  );

}

export default Profile;
